import React, { useEffect, useMemo, useState, useCallback, useContext } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import {Box, Typography, useTheme} from '@mui/material'
import { isStringEqual, or } from '../../../utils'
import { CRASH_ID_KEY } from '../utils'
import {
  getColumnsModel,
  getConfigOptions,
  mapObjectToLabels
} from '../../Statistics/utils'
import { useMergeContext } from '../../../store/mergeContext'
import { useLocation } from 'react-router-dom'
import useHttp from '../../../hooks/useHttp'
import { ThemeContext } from '../../../App'
import {StripedDataGrid} from "../../../components/tables/StripedDataGrid";

const KINDS = {
  HOSPITAL: 'Hospital'
  , INSURANCE: 'Insurance'
}
const KINDS_TO_NUMBER = {
  INSURANCE: 3
  , HOSPITAL: 2

}

/**
 * PossibleMatchGrid component
 * @param {'HOSPITAL' | 'INSURANCE'} type - The type of the grid, default is 'Hospital'
 */
const PossibleMatchGrid = ({ type = 'HOSPITAL' }) => {
  const [matchesRows, setMatchesRows] = useState([])
  const [matches, setMatches] = useState([])
  const [columns, setColumns] = useState([])
  const [columnGroupingModel, setColumnGroupingModel] = useState([])
  const [selectedMatch, setSelectedMatch] = useState(false)
   const { mode, toggleTheme } = useContext(ThemeContext)

  const getKind = type => {
    return KINDS?.[type]
  }

  const { selectedCrash, setSelectedMatchDetailHospital, setSelectedMatchDetailInsurance } =
    useMergeContext()
  const myLocation = useLocation()
  const token = myLocation.state?.token || null
  const { sendRequest } = useHttp()
  const init = useMemo(
    () => myLocation.state?.init || {},
    [myLocation.state?.init]
  )
  const theme = useTheme()

  const KEYS_TO_KEEP_IN_COLUMNS = [
    'Unit id'
    , CRASH_ID_KEY
    , 'Unit id'
    , 'Crash date'
    , 'Crash type'
    , 'Type'
    , 'Vehicle registration year'
    , 'Gender'
    , 'Name'
    , 'Surname'
  ]

  const keysToFunctions = KEYS_TO_KEEP_IN_COLUMNS.map(isStringEqual)
  const keysToKeepFilterFunction = or(...keysToFunctions)

  const getMatches = useCallback(async () => {
    // eslint-disable-next-line no-restricted-globals
    const matchUrl = new URL(`/api/matches/${selectedCrash}`, location)
    const matchUrlReduced = new URL(matchUrl)
    matchUrlReduced.searchParams.set('format', 'table')
    matchUrlReduced.searchParams.set('organization', KINDS_TO_NUMBER?.[type])
    await sendRequest({
      token
      , url: matchUrlReduced
      , manageResData: res => setMatches(res?.matches)
    })
  }, [token, sendRequest, selectedCrash, setMatches, type])

  useEffect(() => {
    if (selectedCrash) {
      getMatches()
    }
  }, [selectedCrash, getMatches])

  useEffect(() => {
    const { configuration } = init
    const configOptions = getConfigOptions(configuration)
    const mappedMatches = matches?.map(match =>
      mapObjectToLabels(match, configOptions)
    )

    const preparedRows = mappedMatches.map((crash, i) => ({
      ...(crash?.road || [])
      , ...(crash?.unit || [])
      , ...(crash?.unit?.people || [])
      , ...(Object.values(crash?.people) || [])
      , ...(crash?.crash || [])
      , ...(crash || [])
      , ...(crash?.candidate_name && { Name: crash?.candidate_name })
      , ...(crash?.candidate_surname && { Surname: crash?.candidate_surname })
      , ...(crash?.candidate_gender && { Gender: crash?.candidate_gender })
      , id: `${crash?.crash?.[CRASH_ID_KEY]}.${i}`
    }))

    setMatchesRows(preparedRows)
  }, [matches, init])

  useEffect(() => {
    const { configuration } = init
    const { columns, columnGroupingModel } = getColumnsModel(configuration)
    setColumns(
      columns
        ?.filter(col => keysToKeepFilterFunction(col?.field))
        ?.map(col => ({ ...col, flex: 1 }))
    )
    setColumnGroupingModel(columnGroupingModel)
  }, [init, keysToKeepFilterFunction])

  useEffect(() => {
    const myMatch = matches?.find(
      m => m?.crash?.[CRASH_ID_KEY] === selectedMatch
    )
    if (type === 'HOSPITAL') {
      setSelectedMatchDetailHospital(myMatch)
    } else if (type === 'INSURANCE') {
      setSelectedMatchDetailInsurance({
        ...myMatch
        , units: myMatch?.units?.map(unit => ({
          ...unit
          , people: []
        }))
        , people: myMatch?.people?.length
          ? myMatch?.people
          : myMatch?.units?.flatMap((u, ui) =>
            u?.people?.flatMap((p, pi) => ({ ...p, unitIndex: ui, personIndex: pi }))
          )
      })
    }
  }, [selectedMatch, matches, type, setSelectedMatchDetailHospital, setSelectedMatchDetailInsurance])

  const paginationModel = { page: 0, pageSize: 25 }

  return (
    <Box>
      <Typography sx={{ width: '100%', mt: 2,mb: 2,color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white',fontSize:20,fontWeight:'bold' }} variant="h5">
        Possible {getKind(type)} matches
      </Typography>
      <StripedDataGrid

        showColumnVerticalBorder
        showCellVerticalBorder
        unstable_rowSpanning
        rows={matchesRows}
        columns={columns}
        editMode="row"
        columnGroupingModel={columnGroupingModel}
        onRowClick={e => setSelectedMatch(e?.row?.[CRASH_ID_KEY])}
        disableMultipleRowSelection
        rowSelection
        initialState={{
          pagination: { paginationModel }
          , columns: { columnVisibilityModel: { id: false } }
        }}
        pageSizeOptions={[10, 25, 50]}
      />
    </Box>
  )
}

export default PossibleMatchGrid
